import React, { useCallback, useEffect, useState } from "react";
import Header from "../../../component/organisms/header";
import MainLayout from "../../../component/atoms/layout/mainLayout";
import Sidebar from "../../../component/organisms/sidebar";
import DetailPageLayout from "../../../component/atoms/layout/detail/detailPageLayout";
import styled from "styled-components";
import TableTitle from "./../../../component/atoms/text/table/tableTitle";
import PretendardText from "../../../component/atoms/text/pretendardText";
import TableText from "./../../../component/atoms/text/table/tableText";
import { formatNumberWithCommas } from "./../../../util/formatNumber";
import ExcelBtn from "./../../../component/atoms/button/excelBtn";
import SelectBox from "../../../component/organisms/selectBox";
import AnalyticsApi from "../../../api/analytics.api";
import getToday from "../../../util/getToday";
import moment from "moment";
import { errorHandler } from "../../../util/errorHandler";

const StatisticsPeriod = () => {
  const today = getToday();

  // ---------- 상단 필터 관련 ---------- //
  const [selectedFilter, setSelectedFilter] = useState({
    dateType: "일자별 요약",
    year: "연도 선택",
    month: "월 선택",
  });

  const [filterBoxOpen, setFilterBoxOpen] = useState({
    dateType: false,
    year: false,
    month: false,
  });

  const dateTypeList = [
    {
      title: "일자별 요약",
      value: "일자별 요약",
    },
    {
      title: "월별 요약",
      value: "월별 요약",
    },
  ];

  // 2024년부터 최대 10년까지
  const yearList = Array.from({ length: 10 }, (_, i) => {
    return {
      title: `${2024 + i}`,
      value: `${2024 + i}`,
    };
  });

  // 1월부터 12월까지
  const monthList = Array.from({ length: 12 }, (_, i) => {
    return {
      title: `${i + 1}월`,
      value: `${i + 1}월`,
    };
  });

  const reducer = useCallback((data, key) => {
    return data.reduce((acc, cur) => {
      return acc + Number(cur[key]);
    }, 0);
  }, []);

  // ---------- 데이터 관련 ---------- //
  const [periodDataList, setPeriodDataList] = useState([]);
  const initialPeriodTableObj = {
    totalAnnualCancelCount: 0,
    totalAnnualCount: 0,
    totalAskCount: 0,
    totalBoardPostCount: 0,
    totalCommentCount: 0,
    totalCouponUseAmount: 0,
    totalLifetimeCancelCount: 0,
    totalLifetimeCount: 0,
    totalMonthCancelCount: 0,
    totalMonthCount: 0,
    totalPointGiveAmount: 0,
    totalPointUseAmount: 0,
    totalRevenue: 0,
    totalReviewCount: 0,
    totalSignupCount: 0,
    totalStoreSalesCount: 0,
    totalVisitCount: 0,
    totalPageViewCount: 0,
  };

  const [periodTableObj, setPeriodTableObj] = useState(initialPeriodTableObj);

  // 데이터 가져오기
  const getPeriodAnalysisList = async (searchDate, startDate) => {
    try {
      const data = {
        page: 1,
        size: 0,
        searchDate: searchDate || today,
        periodType: selectedFilter.dateType === "일자별 요약" ? "day" : "month",
      };

      if (startDate) {
        data.startDate = startDate;
      }

      const response = (await AnalyticsApi.getListAnalytics(data)).data.data
        .content;

      const processedData = response.map((item) => {
        return {
          analyticsDate: item.analyticsDate || item.analyticsdate,
          annualCancelCount: Number(
            item.annualCancelCount || item.annualcancelcount || 0
          ),
          annualCount: Number(item.annualCount || item.annualcount || 0),
          askCount: Number(item.askCount || item.askcount || 0),
          boardPostCount: Number(
            item.boardPostCount || item.boardpostcount || 0
          ),
          commentCount: Number(item.commentCount || item.commentcount || 0),
          couponUseAmount: Number(
            item.couponUseAmount || item.couponuseamount || 0
          ),
          lifetimeCancelCount: Number(
            item.lifetimeCancelCount || item.lifetimecancelcount || 0
          ),
          lifetimeCount: Number(item.lifetimeCount || item.lifetimecount || 0),
          monthCancelCount: Number(
            item.monthCancelCount || item.monthcancelcount || 0
          ),
          monthCount: Number(item.monthCount || item.monthcount || 0),
          pointGiveAmount: Number(
            item.pointGiveAmount || item.pointgiveamount || 0
          ),
          pointUseAmount: Number(
            item.pointUseAmount || item.pointuseamount || 0
          ),
          revenue: Number(item.revenue || 0),
          reviewCount: Number(item.reviewCount || item.reviewcount || 0),
          signupCount: Number(item.signupCount || item.signupcount || 0),
          storeSalesCount: Number(
            item.storeSalesCount || item.storesalescount || 0
          ),
          totalAnnualCount: Number(item.totalAnnualCount || 0),
          totalLifetimeCount: Number(item.totalLifetimeCount || 0),
          totalMonthCount: Number(item.totalMonthCount || 0),
          totalSignupCount: Number(item.totalSignupCount || 0),
          totalStoreSalesCount: Number(item.totalStoreSalesCount || 0),
          visitCount: Number(item.visitCount || item.visitcount || 0),
          pageViewCount: Number(item.pageViewCount || item.pageviewcount || 0),
        };
      });

      setPeriodDataList(processedData);

      const obj = {
        totalAnnualCancelCount: reducer(processedData, "annualCancelCount"),
        totalAnnualCount: reducer(processedData, "annualCount"),
        totalAskCount: reducer(processedData, "askCount"),
        totalBoardPostCount: reducer(processedData, "boardPostCount"),
        totalCommentCount: reducer(processedData, "commentCount"),
        totalCouponUseAmount: reducer(processedData, "couponUseAmount"),
        totalLifetimeCancelCount: reducer(processedData, "lifetimeCancelCount"),
        totalLifetimeCount: reducer(processedData, "lifetimeCount"),
        totalMonthCancelCount: reducer(processedData, "monthCancelCount"),
        totalMonthCount: reducer(processedData, "monthCount"),
        totalPointGiveAmount: reducer(processedData, "pointGiveAmount"),
        totalPointUseAmount: reducer(processedData, "pointUseAmount"),
        totalRevenue: reducer(processedData, "revenue"),
        totalReviewCount: reducer(processedData, "reviewCount"),
        totalSignupCount: reducer(processedData, "signupCount"),
        totalStoreSalesCount: reducer(processedData, "storeSalesCount"),
        totalVisitCount: reducer(processedData, "visitCount"),
        totalPageViewCount: reducer(processedData, "pageViewCount"),
      };

      setPeriodTableObj(obj);

      setSelectedFilter({
        ...selectedFilter,
        year: moment(data.searchDate).format("YYYY"),
        month: moment(data.searchDate).format("M월"),
      });
    } catch (error) {
      errorHandler(error);
    }
  };

  useEffect(() => {
    if (
      (selectedFilter.dateType === "일자별 요약" &&
        (selectedFilter.year === "연도 선택" ||
          selectedFilter.month === "월 선택")) ||
      (selectedFilter.dateType === "월별 요약" &&
        selectedFilter.year === "연도 선택")
    )
      return;

    if (selectedFilter.dateType === "일자별 요약") {
      // 월 추출 (1월부터 9월까지는 앞에 0이 붙음)
      const month =
        selectedFilter.month.split("월")[0].length === 1
          ? `0${selectedFilter.month.split("월")[0]}`
          : selectedFilter.month.split("월")[0];

      // 해당하는 년, 월의 말일 구하기
      const lastDay = moment(
        `${selectedFilter.year}-${month}`,
        "YYYY-M"
      ).daysInMonth();

      const startDate = `${selectedFilter.year}-${month}-${lastDay}`;

      const date = `${selectedFilter.year}-${month}-01`;

      getPeriodAnalysisList(date, startDate);
    } else {
      const date = `${selectedFilter.year}-01-01`;
      getPeriodAnalysisList(date);
    }
  }, [selectedFilter.dateType, selectedFilter.year, selectedFilter.month]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Header />
      <MainLayout
        style={{
          position: "relative",
          gap: 70,
        }}
      >
        <Sidebar />

        {/* 본문 영역 */}
        <DetailPageLayout
          style={{
            width: 1515,
          }}
        >
          <TableTitle>기간별 분석</TableTitle>
          <ContentContainer>
            <ChartContainer>
              <ChartHeader>
                <ChartHeaderText>기간별 분석</ChartHeaderText>
                <div
                  style={{
                    width: "fit-content",
                    display: "flex",
                    gap: 13,
                    alignItems: "center",
                  }}
                >
                  {/* 요약 구분 선택 */}
                  <SelectBox
                    isMiddle={true}
                    selectedValue={selectedFilter.dateType}
                    isOpen={filterBoxOpen.dateType}
                    wrapperHandler={() => {
                      setFilterBoxOpen({
                        dateType: !filterBoxOpen.dateType,
                        year: false,
                        month: false,
                      });
                    }}
                    itemList={dateTypeList}
                    selectedValueSetterFunc={(value) => {
                      setSelectedFilter({
                        ...selectedFilter,
                        dateType: value,
                      });
                      setFilterBoxOpen({
                        dateType: false,
                        year: false,
                        month: false,
                      });
                    }}
                  />
                  {/* 연도 선택 */}
                  <SelectBox
                    isMiddle={true}
                    selectedValue={selectedFilter.year}
                    isOpen={filterBoxOpen.year}
                    wrapperHandler={() => {
                      setFilterBoxOpen({
                        dateType: false,
                        year: !filterBoxOpen.year,
                        month: false,
                      });
                    }}
                    itemList={yearList}
                    selectedValueSetterFunc={(value) => {
                      setSelectedFilter({
                        ...selectedFilter,
                        year: value,
                      });
                      setFilterBoxOpen({
                        dateType: false,
                        year: false,
                        month: false,
                      });
                    }}
                    subMenuStyle={{
                      minHeight: 42,
                    }}
                  />
                  {/* 월 선택 */}
                  {selectedFilter.dateType === "일자별 요약" && (
                    <SelectBox
                      isMiddle={true}
                      selectedValue={selectedFilter.month}
                      isOpen={filterBoxOpen.month}
                      wrapperHandler={() => {
                        setFilterBoxOpen({
                          dateType: false,
                          year: false,
                          month: !filterBoxOpen.month,
                        });
                      }}
                      itemList={monthList}
                      selectedValueSetterFunc={(value) => {
                        setSelectedFilter({
                          ...selectedFilter,
                          month: value,
                        });
                        setFilterBoxOpen({
                          dateType: false,
                          year: false,
                          month: false,
                        });
                      }}
                    />
                  )}
                  {/* <ExcelBtn
                    style={{
                      backgroundColor: "#313233",
                    }}
                  /> */}
                </div>
              </ChartHeader>
              {/* 테이블 영역 */}
              <table
                style={{
                  width: "100%",
                  borderSpacing: 0,
                  borderCollapse: "collapse",
                }}
              >
                <colgroup>
                  {/* 합계: 1515px */}
                  {/* 일자 */}
                  <col width="110px" />
                  {/* 구매수 */}
                  <col width="97px" />
                  {/* 매출액 */}
                  <col width="159px" />
                  {/* 페이지뷰 */}
                  <col width="117px" />
                  {/* 방문자 */}
                  <col width="100px" />
                  {/* 신규 1년회원 */}
                  <col width="119px" />
                  {/* 신규 월간회원 */}
                  <col width="102px" />
                  {/* 가입 */}
                  <col width="75px" />
                  {/* 문의 */}
                  <col width="86px" />
                  {/* 후기 */}
                  <col width="59px" />
                  {/* 새글 */}
                  <col width="61px" />
                  {/* 댓글 */}
                  <col width="56px" />
                  {/* 쿠폰사용액 */}
                  <col width="80px" />
                  {/* 포인트 지급액 */}
                  <col width="125px" />
                  {/* 포인트 사용액 */}
                  <col width="169px" />
                </colgroup>
                <thead>
                  <tr
                    style={{
                      height: 41,
                      backgroundColor: "#313233",
                    }}
                  >
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          paddingLeft: 30,
                        }}
                      >
                        일자
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          textAlign: "right",
                        }}
                      >
                        구매수
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          textAlign: "right",
                        }}
                      >
                        매출액
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          textAlign: "right",
                        }}
                      >
                        페이지뷰
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          textAlign: "right",
                        }}
                      >
                        방문자
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          textAlign: "right",
                        }}
                      >
                        신규 1년회원
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          textAlign: "right",
                        }}
                      >
                        신규 월간회원
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          textAlign: "right",
                        }}
                      >
                        가입
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          textAlign: "right",
                        }}
                      >
                        문의
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          textAlign: "right",
                        }}
                      >
                        후기
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          textAlign: "right",
                        }}
                      >
                        새글
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          textAlign: "right",
                        }}
                      >
                        댓글
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          textAlign: "right",
                        }}
                      >
                        쿠폰사용액
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          textAlign: "right",
                        }}
                      >
                        포인트 지급액
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          textAlign: "right",
                          paddingRight: 57,
                        }}
                      >
                        포인트 사용액
                      </TableText>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {periodDataList.map((item, index) => {
                    return (
                      <>
                        <tr
                          key={index}
                          style={{
                            height: 59,
                            minHeight: 59,
                            borderBottom:
                              "0.5px solid rgba(255, 255, 255, 0.12)",
                          }}
                        >
                          {/* 일자 */}
                          <td>
                            <TableText
                              style={{
                                paddingLeft: 30,
                              }}
                            >
                              {/* 일자별 요약인 경우 "YYYY-MM-DD" 월별 요약인 경우 "2024-MM" 형태로 변환 */}
                              {selectedFilter.dateType === "일자별 요약"
                                ? moment(item.analyticsDate).format(
                                    "YYYY-MM-DD"
                                  )
                                : moment(item.analyticsDate).format("YYYY-MM")}
                            </TableText>
                          </td>
                          {/* 구매수 */}
                          <td>
                            <TableText
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {formatNumberWithCommas(
                                item?.storeSalesCount || 0
                              )}
                            </TableText>
                          </td>
                          {/* 매출액 */}
                          <td>
                            <TableText
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {formatNumberWithCommas(item?.revenue || 0)}
                            </TableText>
                          </td>
                          {/* 페이지뷰 */}
                          <td>
                            <TableText
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {formatNumberWithCommas(item?.pageViewCount || 0)}
                            </TableText>
                          </td>
                          {/* 방문자 */}
                          <td>
                            <TableText
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {formatNumberWithCommas(item?.visitCount || 0)}
                            </TableText>
                          </td>
                          {/* 신규 1년회원 */}
                          <td>
                            <TableText
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {formatNumberWithCommas(item?.annualCount || 0)}
                            </TableText>
                          </td>
                          {/* 신규 월간간회원 */}
                          <td>
                            <TableText
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {formatNumberWithCommas(item?.monthCount || 0)}
                            </TableText>
                          </td>
                          {/* 가입 */}
                          <td>
                            <TableText
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {formatNumberWithCommas(item?.signupCount || 0)}
                            </TableText>
                          </td>
                          {/* 문의 */}
                          <td>
                            <TableText
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {formatNumberWithCommas(item?.askCount || 0)}
                            </TableText>
                          </td>
                          {/* 후기 */}
                          <td>
                            <TableText
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {formatNumberWithCommas(item?.reviewCount || 0)}
                            </TableText>
                          </td>
                          {/* 새글 */}
                          <td>
                            <TableText
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {formatNumberWithCommas(
                                item?.boardPostCount || 0
                              )}
                            </TableText>
                          </td>
                          {/* 댓글 */}
                          <td>
                            <TableText
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {formatNumberWithCommas(item?.commentCount || 0)}
                            </TableText>
                          </td>
                          {/* 쿠폰사용액 */}
                          <td>
                            <TableText
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {formatNumberWithCommas(
                                item?.couponUseAmount || 0
                              )}
                            </TableText>
                          </td>
                          {/* 포인트 지급액 */}
                          <td>
                            <TableText
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {formatNumberWithCommas(
                                item?.pointGiveAmount || 0
                              )}
                            </TableText>
                          </td>
                          {/* 포인트 사용액 */}
                          <td>
                            <TableText
                              style={{
                                textAlign: "right",
                                marginRight: 57,
                              }}
                            >
                              {formatNumberWithCommas(
                                item?.pointUseAmount || 0
                              )}
                            </TableText>
                          </td>
                        </tr>
                      </>
                    );
                  })}
                  {/* 합계 */}
                  <tr
                    style={{
                      height: 59,
                    }}
                  >
                    <td>
                      <TableText
                        style={{
                          fontWeight: "600",
                          letterSpacing: 0,
                          lineHeight: "22.75px",
                          color: "#f3f3f3",
                          paddingLeft: 30,
                        }}
                      >
                        {selectedFilter.dateType === "일자별 요약" &&
                          selectedFilter.year !== "연도 선택" &&
                          selectedFilter.month !== "월 선택" &&
                          `${selectedFilter.month} 합계`}

                        {selectedFilter.dateType === "월별 요약" &&
                          selectedFilter.year !== "연도 선택" &&
                          `${selectedFilter.year} 합계`}

                        {(selectedFilter.year === "연도 선택" ||
                          selectedFilter.month === "월 선택") &&
                          "합계"}
                      </TableText>
                    </td>
                    {/* 구매수 */}
                    <td>
                      <TableText
                        style={{
                          fontWeight: "600",
                          letterSpacing: 0,
                          lineHeight: "22.75px",
                          color: "#f3f3f3",
                          textAlign: "right",
                        }}
                      >
                        {formatNumberWithCommas(
                          periodTableObj.totalStoreSalesCount
                        )}
                      </TableText>
                    </td>
                    {/* 매출액 */}
                    <td>
                      <TableText
                        style={{
                          fontWeight: "600",
                          letterSpacing: 0,
                          lineHeight: "22.75px",
                          color: "#f3f3f3",
                          textAlign: "right",
                        }}
                      >
                        {formatNumberWithCommas(periodTableObj.totalRevenue)}
                      </TableText>
                    </td>
                    {/* 페이지뷰 */}
                    <td>
                      <TableText
                        style={{
                          fontWeight: "600",
                          letterSpacing: 0,
                          lineHeight: "22.75px",
                          color: "#f3f3f3",
                          textAlign: "right",
                        }}
                      >
                        {formatNumberWithCommas(
                          periodTableObj.totalPageViewCount
                        )}
                      </TableText>
                    </td>
                    {/* 방문자 */}
                    <td>
                      <TableText
                        style={{
                          fontWeight: "600",
                          letterSpacing: 0,
                          lineHeight: "22.75px",
                          color: "#f3f3f3",
                          textAlign: "right",
                        }}
                      >
                        {formatNumberWithCommas(periodTableObj.totalVisitCount)}
                      </TableText>
                    </td>
                    {/* 신규 1년회원 */}
                    <td>
                      <TableText
                        style={{
                          fontWeight: "600",
                          letterSpacing: 0,
                          lineHeight: "22.75px",
                          color: "#f3f3f3",
                          textAlign: "right",
                        }}
                      >
                        {formatNumberWithCommas(
                          periodTableObj.totalAnnualCount
                        )}
                      </TableText>
                    </td>
                    {/* 신규 월간간회원 */}
                    <td>
                      <TableText
                        style={{
                          fontWeight: "600",
                          letterSpacing: 0,
                          lineHeight: "22.75px",
                          color: "#f3f3f3",
                          textAlign: "right",
                        }}
                      >
                        {formatNumberWithCommas(periodTableObj.totalMonthCount)}
                      </TableText>
                    </td>
                    {/* 가입 */}
                    <td>
                      <TableText
                        style={{
                          fontWeight: "600",
                          letterSpacing: 0,
                          lineHeight: "22.75px",
                          color: "#f3f3f3",
                          textAlign: "right",
                        }}
                      >
                        {formatNumberWithCommas(
                          periodTableObj.totalSignupCount
                        )}
                      </TableText>
                    </td>
                    {/* 문의 */}
                    <td>
                      <TableText
                        style={{
                          fontWeight: "600",
                          letterSpacing: 0,
                          lineHeight: "22.75px",
                          color: "#f3f3f3",
                          textAlign: "right",
                        }}
                      >
                        {formatNumberWithCommas(periodTableObj.totalAskCount)}
                      </TableText>
                    </td>
                    {/* 후기 */}
                    <td>
                      <TableText
                        style={{
                          fontWeight: "600",
                          letterSpacing: 0,
                          lineHeight: "22.75px",
                          color: "#f3f3f3",
                          textAlign: "right",
                        }}
                      >
                        {formatNumberWithCommas(
                          periodTableObj.totalReviewCount
                        )}
                      </TableText>
                    </td>
                    {/* 새글 */}
                    <td>
                      <TableText
                        style={{
                          fontWeight: "600",
                          letterSpacing: 0,
                          lineHeight: "22.75px",
                          color: "#f3f3f3",
                          textAlign: "right",
                        }}
                      >
                        {formatNumberWithCommas(
                          periodTableObj.totalBoardPostCount
                        )}
                      </TableText>
                    </td>
                    {/* 댓글 */}
                    <td>
                      <TableText
                        style={{
                          fontWeight: "600",
                          letterSpacing: 0,
                          lineHeight: "22.75px",
                          color: "#f3f3f3",
                          textAlign: "right",
                        }}
                      >
                        {formatNumberWithCommas(
                          periodTableObj.totalCommentCount
                        )}
                      </TableText>
                    </td>
                    {/* 쿠폰사용 */}
                    <td>
                      <TableText
                        style={{
                          fontWeight: "600",
                          letterSpacing: 0,
                          lineHeight: "22.75px",
                          color: "#f3f3f3",
                          textAlign: "right",
                        }}
                      >
                        {formatNumberWithCommas(
                          periodTableObj.totalCouponUseAmount
                        )}
                      </TableText>
                    </td>
                    {/* 포인트 지급 */}
                    <td>
                      <TableText
                        style={{
                          fontWeight: "600",
                          letterSpacing: 0,
                          lineHeight: "22.75px",
                          color: "#f3f3f3",
                          textAlign: "right",
                        }}
                      >
                        {formatNumberWithCommas(
                          periodTableObj.totalPointGiveAmount
                        )}
                      </TableText>
                    </td>
                    {/* 포인트 사용 */}
                    <td>
                      <TableText
                        style={{
                          fontWeight: "600",
                          letterSpacing: 0,
                          lineHeight: "22.75px",
                          color: "#f3f3f3",
                          textAlign: "right",
                          marginRight: 57,
                        }}
                      >
                        {formatNumberWithCommas(
                          periodTableObj.totalPointUseAmount
                        )}
                      </TableText>
                    </td>
                  </tr>
                </tbody>
              </table>
            </ChartContainer>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 11,
              }}
            >
              <PretendardText
                style={{
                  fontWeight: "700",
                  fontSize: "16px",
                  lineHeight: "28px",
                  letterSpacing: "-0.3%",
                  color: "#f3f3f3",
                }}
              >
                매출금액 집계 기준
              </PretendardText>
              <ul
                style={{
                  listStyle: "disc",
                  listStylePosition: "inside",
                  listColor: "#aaa",
                }}
              >
                <ListText>
                  결제 완료가 처리된 순간부터 구매일자 기준으로 집계됩니다.
                </ListText>
                <ListText>
                  취소 완료시 결제금액에서 환불금액만큼 회수합니다.
                </ListText>
                <ListText>
                  결제 및 취소,환불 시 항상 구매일자 기준으로 집계 및
                  회수합니다.
                  <br />
                  예: 4월 1일 구매를 4월 15일에 취소처리해도 4월 1일 매출에서
                  차감
                </ListText>
                <ListText>
                  포인트, 쿠폰 등 각종 할인금액은 매출에 반영되지 않으며 실결제
                  금액이 반영됩니다.
                </ListText>
                <ListText>
                  <span
                    style={{
                      color: "#cf6679",
                    }}
                  >
                    상기 데이터는 전반적인 영업현황을 기술적으로 나타낸
                    참고용이며 세금신고 및 외부고시를 위한 자료로 활용하실 수
                    없습니다.
                  </span>
                </ListText>
                <ListText>
                  부가세 신고 자료는 가입하신 PG사(이니시스, 나이스페이, KCP,
                  네이버페이 등)에서 제공하는 자료와 국세청 홈텍스 현금영수증 및
                  전자세금계산서 발급내역을 확인해 주시기 바랍니다.
                </ListText>
              </ul>
            </div>
          </ContentContainer>
        </DetailPageLayout>
      </MainLayout>
    </>
  );
};

export default StatisticsPeriod;

const ContentContainer = styled.div.attrs((props) => {})`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 32px;
`;

const ChartContainer = styled.div.attrs((props) => {})`
  width: 100%;
  height: fit-content;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 40px;
  border-radius: 10px;
  background: #272829;
  box-shadow: 2px 2px 10px 0px rgba(0, 0, 0, 0.08);
`;

const ChartHeader = styled.div.attrs((props) => {})`
  width: 100%;
  height: 68px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  padding: 0px 30px 20px 30px;
`;

const ChartHeaderText = styled(PretendardText).attrs((props) => {})`
  font-weight: 700;
  font-size: 16px;
  line-height: 28px;
  letter-spacing: -0.3%;
  color: #f3f3f3;
`;

const ListText = styled.li.attrs((props) => {})`
  font-family: "Pretendard";
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  color: #aaa;
`;
